import { observer } from "mobx-react-lite";
import MainLayout, { MAIN_RIGHT_TOP_HEIGHT } from "../MainLayout"; // ★ 추가: HEIGHT 상수
import { useNavigate } from "react-router-dom";
import { VerticalLayout } from "../../common/common-layout";
import { TitleLargeMedium } from "../../common/common-text";
import { useEffect, useState } from "react";
import { fetchNoticeList, requestDeleteAllFiles, requestDeleteNoticeDocuments } from "../../service/BoardService";
import BTBox from "../../common/components/BTBox";
import BTList from "../../common/components/board/BTList";
import { GridRowParams } from "@mui/x-data-grid";
import Notice from "../../model/board/Notice";
import { formatDateToYMD } from "../../common/utils/DateUtils";
import { dialogStore } from "../../store/DialogStore";

const NoticeBoard = () => {
  return (
    <MainLayout>
      <NoticeBoardBody />
    </MainLayout>
  );
};

export default NoticeBoard;

const NoticeBoardBody = observer(() => {
  const navigate = useNavigate();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const loadData = async () => {
    const result = await fetchNoticeList();
    setNotices(result);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleWriteClick = () => {
    navigate("/board/notice/add");
  };

  const handleDeleteClick = async (selectedIndexes: number[]) => {
    if (selectedIndexes.length === 0) {
      dialogStore.showDialog({
        title: "알림",
        message: "삭제할 아이템을 선택해주세요.",
        onConfirm: () => {},
        onClose: () => {},
      });
      return;
    }

    const deleteConfirmed = await dialogStore.showAsyncDialog({
      title: "경고",
      message: `선택된 ${selectedIndexes.length}개의 공지사항을 정말로 삭제하시겠습니까?`,
      isCaution: true,
    });

    if (!deleteConfirmed) return;

    try {
      const selectedNoticeIds: string[] = selectedIndexes
        .map((idx) => notices[idx]?.id)
        .filter((id): id is string => !!id);

      await requestDeleteNoticeDocuments(selectedNoticeIds);
      await Promise.all(selectedNoticeIds.map((docId) => requestDeleteAllFiles("notice", docId)));

      dialogStore.showDialog({
        title: "삭제 완료",
        message: `${selectedNoticeIds.length}개의 공지사항이 삭제되었습니다.`,
        onConfirm: () => {
          setSelectedRows([]);
          loadData();
        },
        onClose: () => {
          setSelectedRows([]);
          loadData();
        },
      });
    } catch (e) {
      console.error("공지사항 삭제 실패", e);
      dialogStore.showDialog({
        title: "오류",
        message: "공지사항 삭제 중 오류가 발생했습니다.",
        onConfirm: () => {},
        onClose: () => {},
      });
    }
  };

  const columns = [
    { field: "id", headerName: "번호", width: 80, headerAlign: "center", align: "center" },
    { field: "title", headerName: "제목", flex: 1, headerAlign: "center", align: "left" },
    { field: "postDate", headerName: "게시일자", width: 120, headerAlign: "center", align: "center" },
    { field: "isAnchored", headerName: "상단 고정 여부", width: 150, headerAlign: "center", align: "center" },
    { field: "isPublished", headerName: "공개 여부", width: 120, headerAlign: "center", align: "center" },
  ];

  const rows = notices.map((notice, index) => ({
    id: notices.length - index,
    title: notice.title,
    postDate: notice.postDate ? formatDateToYMD(notice.postDate) : "-",
    isAnchored: notice.isAnchored ? "✅" : "❌",
    isPublished: notice.isPublished ? "✅" : "❌",
    originalNotice: notice,
  }));

  const handleRowClick = (params: GridRowParams): void => {
    const matchedNotice = params.row.originalNotice;
    if (matchedNotice) {
      navigate("/board/notice/detail", {
        state: { notice: matchedNotice },
      });
    }
  };

  return (
    // ★ 바깥 래퍼: 화면 높이 보장 + column
    <div
      style={{
        width: "100%",
        minHeight: `calc(100vh - ${MAIN_RIGHT_TOP_HEIGHT})`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ★ 내부 컨테이너: 패딩 + 레이아웃 안정화 */}
      <VerticalLayout
        height="auto"
        paddingLeft={36}
        paddingRight={36}
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <TitleLargeMedium>공지사항 게시판</TitleLargeMedium>
        <BTBox height="36px" />

        {/* ★ 리스트 래퍼: width 100% + 필요 시 여기서 스크롤 */}
        <div style={{ width: "100%" }}>
          <BTList
            rows={rows}
            columns={columns}
            checkboxSelection={true}
            handleRowClick={handleRowClick}
            onWriteButtonClick={handleWriteClick}
            onDeleteButtonClick={handleDeleteClick}
            writeButtonText={"추가"}
            deleteButtonText={"삭제"}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            style={{ flex: 1, minHeight: 0, width: "100%" }}
            pageStateKey="board.notice.list"
          />
        </div>
      </VerticalLayout>
    </div>
  );
});